import { Base, withMountWhenInView } from '@studiometa/js-toolkit';
import { damp, matrix, nextFrame, removeClass } from '@studiometa/js-toolkit/utils';

/**
 * @typedef {object} ParallaxImageRef
 * @property {HTMLElement} target
 */

/**
 * @typedef {object} ParallaxImageInterface
 * @property {ParallaxImageRef} $refs
 */

/**
 * ParallaxImage class.
 */
export default class ParallaxImageDesktopHorizontal extends withMountWhenInView(Base, {
  rootMargin: '50%',
}) {
  /**
   * Config.
   */
  static config = {
    refs: ['target'],
    options: {
      ratio: {
        type: Number,
        default: 0,
      },
    },
  };

  /**
   * Translate X.
   * @type {number}
   */
  translateX = 0;

  /**
   * Scroll progress X.
   * @type {number}
   */
  progressX = 0;

  /**
   * Transform values.
   */
  transform = {
    translateX: 0,
  };

  /**
   * Element left.
   */
  left = 100;

  /**
   * Set left on mounted & remove 'opacity-0' class.
   * @this    {ParallaxImage & ParallaxImageInterface}
   * @returns {void}
   */
  mounted() {
    nextFrame(() => {
      this.left = this.$el.getBoundingClientRect().left + window.scrollX;
    });
    removeClass(this.$el, 'opacity-0');
  }

  /**
   * Update delta scroll on scroll.
   * @param   {import('@studiometa/js-toolkit/services/scroll').ScrollServiceProps} props
   * @this    {ParallaxImage & ParallaxImageInterface}
   * @returns {void}
   */
  scrolled(props) {
    this.progressX =
      (props.x + window.innerWidth - this.left) / (this.$el.offsetWidth + window.innerWidth);
  }

  /**
   * Set top on resize.
   * @this    {ParallaxImage & ParallaxImageInterface}
   * @returns {void}
   */
  resized() {
    this.left = this.$el.getBoundingClientRect().left + window.scrollX;
    this.draw();
  }

  /**
   * Update values on raf.
   * @this    {ParallaxImage & ParallaxImageInterface}
   * @returns {void}
   */
  ticked() {
    this.draw();
  }

  /**
   * Draw function.
   * @this    {ParallaxImage & ParallaxImageInterface}
   * @returns {void}
   */
  draw() {
    this.translateX = (this.progressX - 0.5) * this.$options.ratio * this.$el.offsetWidth;
    this.transform.translateX = damp(this.translateX, this.transform.translateX, 0.1);

    // Defer DOM update to the next frame
    nextFrame(() => {
      this.$refs.target.style.transform = `${matrix(this.transform)} translateZ(0px)`;
    });
  }
}
